import { QueryClient } from '@tanstack/react-query'
import { createRootRouteWithContext, createRoute, redirect } from '@tanstack/react-router'

import { StaticRouteParameter, DynamicRouteParameterWPrefix } from 'config/routing'
import { Root } from 'domains/ProcessInvitation'
import { App } from 'pages/AppInit/App'
import { AppShell } from 'pages/AppInit/Logging'

type NWRouterContext = {
  queryClient: QueryClient
}

/* START: Public routes */
export const rootRoute = createRootRouteWithContext<NWRouterContext>()({
  component: App,
})

export const appShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'appShell',
  component: AppShell,
})

type RedirectToProcessInvitationSearch = {
  invitationToken: string
  invitationId: string
}

export const redirectToOrganizationProcessInvitationRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: `${StaticRouteParameter.ORGANIZATIONS}/${DynamicRouteParameterWPrefix.ORGANIZATION_ID}/${StaticRouteParameter.ACCEPT_INVITATION}`,
  validateSearch: (search: Record<string, unknown>): RedirectToProcessInvitationSearch => ({
    invitationId: String(search.invitationId),
    invitationToken: String(search.invitationToken),
  }),
  beforeLoad: ({ location: { pathname }, params: { organizationId }, search }) => {
    throw redirect({
      replace: true,
      from: redirectToOrganizationProcessInvitationRoute.fullPath,
      to: processInvitationRoute.to,
      search: {
        ...search,
        referrer: pathname.replace(`/${StaticRouteParameter.ACCEPT_INVITATION}`, ''),
        origin: 'organization',
        organizationId,
      },
    })
  },
})

export const redirectToEnvironmentProcessInvitationRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: `${StaticRouteParameter.ORGANIZATIONS}/${DynamicRouteParameterWPrefix.ORGANIZATION_ID}/${StaticRouteParameter.ENVIRONMENTS}/${DynamicRouteParameterWPrefix.ENVIRONMENT_ID}/${StaticRouteParameter.ACCEPT_INVITATION}`,
  validateSearch: (search: Record<string, unknown>): RedirectToProcessInvitationSearch => ({
    invitationId: String(search.invitationId),
    invitationToken: String(search.invitationToken),
  }),
  beforeLoad: ({ location: { pathname }, params: { environmentId }, search }) => {
    throw redirect({
      replace: true,
      from: redirectToEnvironmentProcessInvitationRoute.fullPath,
      to: processInvitationRoute.to,
      search: {
        ...search,
        referrer: pathname.replace(`/${StaticRouteParameter.ACCEPT_INVITATION}`, ''),
        origin: 'environment',
        environmentId,
      },
    })
  },
})

export const redirectToWorkspaceProcessInvitationRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: `${StaticRouteParameter.ORGANIZATIONS}/${DynamicRouteParameterWPrefix.ORGANIZATION_ID}/${StaticRouteParameter.ENVIRONMENTS}/${DynamicRouteParameterWPrefix.ENVIRONMENT_ID}/${StaticRouteParameter.WORKSPACES}/${DynamicRouteParameterWPrefix.WORKSPACE_ID}/${StaticRouteParameter.ACCEPT_INVITATION}`,
  validateSearch: (search: Record<string, unknown>): RedirectToProcessInvitationSearch => ({
    invitationId: String(search.invitationId),
    invitationToken: String(search.invitationToken),
  }),
  beforeLoad: ({ location: { pathname }, params: { workspaceId }, search }) => {
    throw redirect({
      replace: true,
      from: redirectToWorkspaceProcessInvitationRoute.fullPath,
      to: processInvitationRoute.to,
      search: {
        ...search,
        referrer: pathname.replace(`/${StaticRouteParameter.ACCEPT_INVITATION}`, ''),
        origin: 'workspace',
        workspaceId,
      },
    })
  },
})

// TODO: Rebuild invitation redirect flow without Azure AD B2C
export const processInvitationADRedirectHashProcessRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: StaticRouteParameter.PROCESS_INVITATION_AD_REDIRECT,
  beforeLoad: () => {
    throw redirect({ to: '/' })
  },
})

export const processInvitationADLogoutRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: `${StaticRouteParameter.PROCESS_INVITATION_AD_REDIRECT}/${StaticRouteParameter.LOGOUT}`,
  beforeLoad: () => {
    throw redirect({ to: '/' })
  },
})

type ProcessInvitationSearch = {
  invitationId: string
  invitationToken: string
  referrer: string
  origin: 'organization' | 'environment' | 'workspace'
  organizationId?: string
  environmentId?: string
  workspaceId?: string
}

export const processInvitationRoute = createRoute({
  getParentRoute: () => appShellRoute,
  path: StaticRouteParameter.PROCESS_INVITATION,
  component: Root,
  validateSearch: (search: Record<string, unknown>): ProcessInvitationSearch => {
    const { invitationId, invitationToken, referrer, origin, organizationId, environmentId, workspaceId } = search

    return {
      invitationId: String(invitationId ?? ''),
      invitationToken: String(invitationToken ?? ''),
      referrer: String(referrer ?? ''),
      origin: String(origin ?? 'organization') as ProcessInvitationSearch['origin'],
      ...(!!organizationId && { organizationId: String(organizationId) }),
      ...(!!environmentId && { environmentId: String(environmentId) }),
      ...(!!workspaceId && { workspaceId: String(workspaceId) }),
    }
  },
})
/* END: Public routes */
